import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { faChartBar } from '@fortawesome/free-solid-svg-icons';
import { ColorNames, ColorShades, HorizontalGroupedBarChartDataModel, HorizontalGroupedBarChartOptions, Utils } from 'marketpro-design-lib';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NameValuePair } from '../../models/name-value-pair.model';

export type ChartDataFilters = {
  dataLens: NameValuePair;
  projections: number[];
}

@Component({
  selector: 'mpro-region-comparison-chart',
  templateUrl: './region-comparison-chart.component.html',
  styleUrls: ['./region-comparison-chart.component.scss']
})
export class RegionComparisonChartComponent implements OnChanges, OnDestroy {
  public faChart = faChartBar;
  @Input() public isCategories: boolean;
  @Input() public currency: number;
  @Input() public selected: string | undefined = undefined;
  @Input() public chartData: HorizontalGroupedBarChartDataModel[] = [];
  @Input() public selectedYear: number;
  @Output() public chartDataFiltersChanged: EventEmitter<ChartDataFilters> = new EventEmitter();

  public groupedChartOptions: HorizontalGroupedBarChartOptions;
  public projections: number[] = [];
  public dataLensOptions: NameValuePair[] = [];
  public regionComparisonChartOptions: UntypedFormGroup;

  private ngUnsubscribe: Subject<void> = new Subject();

  constructor(private fb: UntypedFormBuilder) {
    this.groupedChartOptions = {
      colors: [
        { name: ColorNames.Blue, shade: ColorShades.Shade400 },
        { name: ColorNames.Orange, shade: ColorShades.Shade400 },
        { name: ColorNames.Fuchsia, shade: ColorShades.Shade400 },
        { name: ColorNames.Teal, shade: ColorShades.Shade400 },
        { name: ColorNames.Purple, shade: ColorShades.Shade400 },
        { name: ColorNames.Green, shade: ColorShades.Shade400 },
        { name: ColorNames.Red, shade: ColorShades.Shade400 },
        { name: ColorNames.Apricot, shade: ColorShades.Shade400 },
      ],
      dataAxisStep: 5,
      tickFormat: this.tickFormat.bind(this)
    };
  }

  public ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public get dropdownLabel(): NameValuePair {
    return this.regionComparisonChartOptions.value.dataLens;
  }

  public tickFormat(tick: any): string {
    const formattedTick = Utils.formatNumber(tick, true);
    const currentDataLensValue = this.regionComparisonChartOptions.value.dataLens.value;
    if (currentDataLensValue !== 'people' && currentDataLensValue !== 'spending_ppp') {
      return `${formattedTick}%`;
    }

    return formattedTick;
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedYear?.firstChange) {
      this.initializeForm();
    }

    if (changes.selectedYear?.currentValue) {
      this.updateDataLensOptions();
      this.regionComparisonChartOptions.patchValue({
        projections: [this.selectedYear]
      });
    }

    if (changes.selected) {
      this.groupedChartOptions = { ...this.groupedChartOptions, selectedGroup: this.selected };
    }
  }

  public dataLensChanged(dataLens: NameValuePair) {
    this.regionComparisonChartOptions.patchValue({ dataLens });
    if (dataLens.value === 'growthUntilSelected' || dataLens.value === 'growthToEndYear') {
      this.regionComparisonChartOptions.controls.projections.disable();
      this.regionComparisonChartOptions.controls.projections.patchValue([this.selectedYear]);
    } else {
      this.regionComparisonChartOptions.controls.projections.enable();
    }
  }

  public showChart(): boolean {
    return this.chartData.length > 0 && this.regionComparisonChartOptions.getRawValue().projections?.length > 0;
  }

  private initializeForm(): void {
    this.regionComparisonChartOptions = this.fb.group({
      dataLens: [new NameValuePair('People', 'people')],
      projections: [[this.selectedYear]]
    });

    this.regionComparisonChartOptions.valueChanges
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(_ => this.chartDataFiltersChanged.emit(this.regionComparisonChartOptions.getRawValue()));
  }

  private updateDataLensOptions(): void {
    this.dataLensOptions = [
      new NameValuePair('People', 'people'),
      this.isCategories ? new NameValuePair('Demand', 'spending_ppp')
        : this.currency===1 ? new NameValuePair('Spending Nominal', 'spending_ppp')
          : new NameValuePair('Spending PPP', 'spending_ppp'),
      new NameValuePair('Spending share', 'spending_share'),
      new NameValuePair(`Growth Rate (until ${this.selectedYear})`, 'growthUntilSelected'),
      new NameValuePair(`Growth Rate (${this.selectedYear}-2034)`, 'growthToEndYear')
    ];

    const currentDataLensValue = this.regionComparisonChartOptions.value.dataLens.value;
    if (currentDataLensValue === 'growthUntilSelected' || currentDataLensValue === 'growthToEndYear') {
      this.dataLensChanged(<NameValuePair>this.dataLensOptions.find(dataLens => dataLens.value === currentDataLensValue))
    }
  }
}
